import { useCallback, useMemo, useState, useEffect } from 'react'
import { handleBridgeTransfer as handleBridgeTransferUtil } from 'helpers/bridgeTransfer'
import { getLDClient } from 'helpers/launchDarklyClient'

export type UseBridgeTransferParams = {
  idToken: string
  employeePublicKey: string
  isImpersonated: boolean
  onError: (message: string) => void
  clientPublicKey: string
}

/**
 * Gets the bridge flag value from LaunchDarkly
 * @returns The bridge flag value or null if not available
 */
const getBridgeFlag = (): {
  clientPublicKey?: { [key: string]: boolean }
  employeePublicKey?: { [key: string]: boolean }
} | null => {
  try {
    const ldClient = getLDClient()
    const flag = ldClient.variation('enrollme.uses-bridge-to-employee-experience-shop-for-health', {
      clientPublicKey: {},
      employeePublicKey: {},
    })
    return flag
  } catch (error) {
    console.error('Failed to get bridge flag from LaunchDarkly:', error)
    return null
  }
}

/**
 * Checks if bridge transfer should be used based on feature flag and 'monkey' search param
 * @param clientPublicKey - The client public key
 * @param employeePublicKey - The employee public key
 * @returns true if bridge transfer should be used
 */
const shouldUseBridgeTransfer = ({
  clientPublicKey,
  employeePublicKey,
}: {
  clientPublicKey: string
  employeePublicKey: string
}): boolean => {
  // Check for 'monkey' search param
  try {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.has('monkey')) {
      return true
    }
  } catch {
    // If URLSearchParams fails, continue to check feature flag
  }

  // Get bridge flag from LaunchDarkly
  const bridgeFlag = getBridgeFlag()

  // Check feature flag
  if (!bridgeFlag) {
    return false
  }

  const clientEnabled = bridgeFlag.clientPublicKey?.[clientPublicKey] === true
  const employeeEnabled = bridgeFlag.employeePublicKey?.[employeePublicKey] === true

  return clientEnabled || employeeEnabled
}

export type TransferringButtonType = 'plan-select' | 'new-plan' | 'qle' | null

export const useBridgeTransfer = ({
  idToken,
  employeePublicKey,
  isImpersonated,
  onError,
  clientPublicKey,
}: UseBridgeTransferParams) => {
  const [transferringGroupId, setTransferringGroupId] = useState<string | null>(null)
  const [transferringButtonType, setTransferringButtonType] = useState<TransferringButtonType>(null)

  const shouldUseBridge = useMemo(
    () =>
      shouldUseBridgeTransfer({
        clientPublicKey,
        employeePublicKey,
      }),
    [clientPublicKey, employeePublicKey]
  )

  const clearTransferring = useCallback(() => {
    setTransferringGroupId(null)
    setTransferringButtonType(null)
  }, [])

  // Reset state when component unmounts
  useEffect(() => {
    return clearTransferring
  }, [clearTransferring])

  // Reset state when page is restored from bfcache (browser back button)
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        clearTransferring()
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [clearTransferring])

  const runBridgeTransfer = useCallback(
    async (groupId: string | undefined, buttonType: TransferringButtonType, target?: string) => {
      setTransferringGroupId(groupId ?? null)
      setTransferringButtonType(buttonType)

      try {
        await handleBridgeTransferUtil({
          idToken,
          employeePublicKey,
          isImpersonated,
          onError: (errorMessage) => {
            clearTransferring()
            onError(errorMessage)
          },
          groupId,
          target,
        })
      } catch (error) {
        console.error('Failed to transfer to employee experience:', error)
        clearTransferring()
        onError(error?.message || 'Failed to transfer. Please try again.')
      }
    },
    [idToken, employeePublicKey, isImpersonated, onError, clearTransferring]
  )

  const handleBridgeTransfer = useCallback(
    (groupId?: string) => runBridgeTransfer(groupId, 'plan-select'),
    [runBridgeTransfer]
  )

  const handleNewPlanBridgeTransfer = useCallback(
    (groupId?: string) => runBridgeTransfer(groupId, 'new-plan'),
    [runBridgeTransfer]
  )

  const handleQLEBridgeTransfer = useCallback(
    (groupId?: string) => runBridgeTransfer(groupId, 'qle', 'qle'),
    [runBridgeTransfer]
  )

  return {
    handleBridgeTransfer,
    handleNewPlanBridgeTransfer,
    transferringGroupId,
    transferringButtonType,
    isTransferring: transferringGroupId !== null,
    shouldUseBridge,
    handleQLEBridgeTransfer,
  }
}
